import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CheckCircle2, ArrowRight, Phone, Mail, Zap, Shield, Wifi, Clock } from "lucide-react";
import { useTranslation } from "@/i18n/translations";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { serviceSchema, localBusinessSchema, breadcrumb, organizationSchema } from "@/i18n/schemaData";

// City-specific content optimized for local SEO Swiss searches
const CITIES = {
  geneve: {
    name: "Genève",
    canton: "GE",
    image: "https://images.unsplash.com/photo-1530841344095-5d8e7f1a8e87?w=1600&q=80&auto=format&fit=crop",
    intro: "Terminal de paiement gratuit pour commerçants à Genève. Que vous teniez un restaurant aux Pâquis, une boutique aux Eaux-Vives, un commerce à Carouge ou Plainpalais, AyaPay équipe les TPE et PME genevoises avec des terminaux Twint, Apple Pay et cartes — sans frais d'achat.",
    neighborhoods: ["Pâquis", "Eaux-Vives", "Carouge", "Plainpalais", "Champel", "Servette", "Cornavin", "Plan-les-Ouates", "Vernier", "Meyrin"],
    sectors: ["Restaurants", "Cafés & brasseries", "Boutiques de mode", "Horlogerie & bijouterie", "Salons de coiffure", "Food trucks", "Taxis & VTC", "Marchés"],
    testimonial: { name: "Marc R.", business: "Restaurateur, Eaux-Vives", text: "Depuis qu'on est passé à AyaPay, on a divisé nos frais de transaction. Et le support en français, c'est appréciable !" },
  },
  lausanne: {
    name: "Lausanne",
    canton: "VD",
    image: "https://images.unsplash.com/photo-1518611507436-f9221403cca2?w=1600&q=80&auto=format&fit=crop",
    intro: "Terminal de paiement 100% gratuit pour commerçants à Lausanne et dans le canton de Vaud. AyaPay équipe restaurants, boutiques, salons et services à Ouchy, Flon, St-François, Renens, Pully et toute la région lémanique avec Twint, Apple Pay et cartes.",
    neighborhoods: ["Flon", "St-François", "Ouchy", "Sous-Gare", "Chauderon", "Renens", "Pully", "Prilly", "Morges", "Vevey"],
    sectors: ["Restaurants & gastronomie", "Cafés & bars", "Boutiques", "Coiffure & esthétique", "Sport & loisirs", "Food trucks", "Marchés artisanaux", "Hôtellerie"],
    testimonial: { name: "Sophie B.", business: "Boutique, Flon", text: "Le terminal est arrivé en 3 jours, gratuit, et l'app AyaPay me donne toutes les stats que je veux. Service client suisse et réactif." },
  },
  zurich: {
    name: "Zurich",
    canton: "ZH",
    image: "https://images.unsplash.com/photo-1530060851226-edcbe10e8d28?w=1600&q=80&auto=format&fit=crop",
    intro: "Kostenloses Zahlungsterminal für Händler in Zürich und Umgebung. AyaPay rüstet Restaurants, Boutiquen, Cafés und Dienstleister in Kreis 1-9, Oerlikon, Altstetten und ganz Zürich mit Twint, Apple Pay und Kartenakzeptanz aus — ohne Kaufkosten.",
    neighborhoods: ["Altstadt", "Niederdorf", "Kreis 4", "Kreis 5 / Zürich-West", "Oerlikon", "Altstetten", "Wiedikon", "Seefeld", "Winterthur", "Uster"],
    sectors: ["Restaurants & Gastronomie", "Boutiquen", "Cafés & Bars", "Friseur & Beauty", "Food Trucks", "Märkte", "Taxis", "Hotellerie"],
    testimonial: { name: "Thomas K.", business: "Café, Zürich-West", text: "Endlich faire Gebühren und ein moderner Terminal mit Twint. Der Schweizer Support antwortet sofort." },
  },
};

export default function LocalPage() {
  const location = useLocation();
  const { lang } = useTranslation();
  // Detect city from URL path: /terminal-paiement-{city}
  const slug = location.pathname.replace("/terminal-paiement-", "").replace("/", "");
  const city = CITIES[slug] ? slug : "geneve";
  const data = CITIES[city];

  const titles = {
    fr: { tag: `Terminal de paiement ${data.name}`, h1: `Terminal de paiement 100% gratuit à ${data.name}`, neigh: "Nous équipons les commerces dans tous les quartiers", sectors: "Adapté à votre secteur", whyUs: "Pourquoi les commerçants de " + data.name + " choisissent AyaPay", contact: "Demander mon terminal gratuit" },
    de: { tag: `Zahlungsterminal ${data.name}`, h1: `100% kostenloses Zahlungsterminal in ${data.name}`, neigh: "Wir bedienen Händler in allen Bezirken", sectors: "Für Ihre Branche optimiert", whyUs: `Warum Händler in ${data.name} AyaPay wählen`, contact: "Mein kostenloses Terminal anfragen" },
    it: { tag: `Terminale di pagamento ${data.name}`, h1: `Terminale gratuito a ${data.name}`, neigh: "Serviamo commercianti in tutti i quartieri", sectors: "Adatto al vostro settore", whyUs: `Perché i commercianti di ${data.name} scelgono AyaPay`, contact: "Richiedi il mio terminale gratuito" },
    pt: { tag: `Terminal ${data.name}`, h1: `Terminal de pagamento gratuito em ${data.name}`, neigh: "Servimos comerciantes em todos os bairros", sectors: "Adaptado ao seu setor", whyUs: `Porque os comerciantes de ${data.name} escolhem AyaPay`, contact: "Pedir o meu terminal gratuito" },
    en: { tag: `Payment terminal ${data.name}`, h1: `100% free payment terminal in ${data.name}`, neigh: "We equip merchants in every district", sectors: "Tailored to your sector", whyUs: `Why ${data.name} merchants choose AyaPay`, contact: "Request my free terminal" },
    tr: { tag: `${data.name} ödeme terminali`, h1: `${data.name}'de %100 ücretsiz ödeme terminali`, neigh: "Tüm semtlerde tüccarlara hizmet veriyoruz", sectors: "Sektörünüze özel", whyUs: `${data.name} tüccarları neden AyaPay'i seçiyor`, contact: "Ücretsiz terminalimi talep et" },
  };
  const tt = titles[lang] || titles.fr;

  const benefits = {
    fr: [
      { icon: Zap, title: "Terminal livré en 3-5 jours", text: "Inscription 100% digitale, signature électronique, livraison rapide partout en " + data.name + "." },
      { icon: Shield, title: "Conformité suisse FINMA & PCI-DSS", text: "Société 100% suisse à Bienne, conformité LPD, RGPD et PCI-DSS Niveau 1." },
      { icon: Wifi, title: "Connectivité 4G/5G + WiFi", text: "SIM 5G incluse — vos paiements fonctionnent partout, même en terrasse, marché ou food truck." },
      { icon: Clock, title: "Support 24/7 en français", text: "Une équipe suisse joignable au 076 200 36 12, pas de hotline délocalisée." },
    ],
    de: [
      { icon: Zap, title: "Lieferung in 3-5 Tagen", text: "100% digitale Anmeldung, elektronische Signatur, schnelle Lieferung in " + data.name + "." },
      { icon: Shield, title: "Schweizer FINMA- und PCI-DSS-Konformität", text: "100% Schweizer Firma in Biel, DSG-, DSGVO- und PCI-DSS Level 1 konform." },
      { icon: Wifi, title: "4G/5G + WiFi Konnektivität", text: "5G-SIM inklusive — Zahlungen funktionieren überall." },
      { icon: Clock, title: "24/7 Support auf Deutsch", text: "Schweizer Team erreichbar unter 076 200 36 12." },
    ],
    it: [
      { icon: Zap, title: "Consegna in 3-5 giorni", text: "Registrazione 100% digitale, firma elettronica." },
      { icon: Shield, title: "Conformità svizzera FINMA & PCI-DSS", text: "Azienda 100% svizzera a Bienne." },
      { icon: Wifi, title: "Connettività 4G/5G + WiFi", text: "SIM 5G inclusa." },
      { icon: Clock, title: "Supporto 24/7", text: "Team svizzero al 076 200 36 12." },
    ],
    pt: [
      { icon: Zap, title: "Entrega em 3-5 dias", text: "Inscrição 100% digital, assinatura eletrónica." },
      { icon: Shield, title: "Conformidade FINMA & PCI-DSS", text: "Empresa 100% suíça em Bienne." },
      { icon: Wifi, title: "Conectividade 4G/5G + WiFi", text: "SIM 5G incluído." },
      { icon: Clock, title: "Suporte 24/7", text: "Equipa suíça no 076 200 36 12." },
    ],
    en: [
      { icon: Zap, title: "Delivered in 3-5 days", text: "100% digital signup, e-signature, fast delivery in " + data.name + "." },
      { icon: Shield, title: "Swiss FINMA & PCI-DSS compliance", text: "100% Swiss company in Biel, FADP/GDPR/PCI-DSS L1." },
      { icon: Wifi, title: "4G/5G + WiFi connectivity", text: "5G SIM included — works anywhere." },
      { icon: Clock, title: "24/7 Swiss support", text: "Swiss team at 076 200 36 12." },
    ],
    tr: [
      { icon: Zap, title: "3-5 günde teslimat", text: "Hızlı dijital kayıt." },
      { icon: Shield, title: "İsviçre FINMA & PCI-DSS uyumu", text: "%100 İsviçre şirketi." },
      { icon: Wifi, title: "4G/5G + WiFi", text: "5G SIM dahil." },
      { icon: Clock, title: "7/24 destek", text: "İsviçre ekibi 076 200 36 12." },
    ],
  };
  const items = benefits[lang] || benefits.fr;

  const schemas = useMemo(() => [
    organizationSchema,
    { ...localBusinessSchema, name: `AyaPay ${data.name}` },
    serviceSchema(
      `Terminal de paiement gratuit ${data.name}`,
      `Solution de paiement complète pour les commerçants de ${data.name} : terminal gratuit, Twint, Apple Pay, NFC, support 24/7.`,
      data.name
    ),
    breadcrumb([
      { name: "Accueil", path: "/" },
      { name: data.name, path: `/${city}` },
    ]),
  ], [city, data.name]);

  return (
    <div data-testid={`local-page-${city}`} className="bg-white">
      <SEO page={`local-${city}`} />
      <JsonLd data={schemas} />

      {/* Hero */}
      <section className="relative bg-[#0F1B3F] text-white pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={data.image} alt={`${data.name} ${data.canton}`} loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00C853]/15 text-[#00C853] text-xs font-semibold tracking-wide uppercase mb-6 border border-[#00C853]/30">
              <MapPin size={14} /> {tt.tag} ({data.canton})
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-4xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              {tt.h1}
            </h1>
            <p className="text-base md:text-lg text-slate-300 max-w-3xl leading-relaxed mb-8">{data.intro}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="local-cta-contact" className="inline-flex items-center gap-2 bg-[#00C853] hover:bg-[#00B248] text-white font-semibold px-6 py-3 rounded-full transition-colors">
                {tt.contact} <ArrowRight size={18} />
              </Link>
              <a href="tel:+41762003612" data-testid="local-cta-call" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white font-semibold px-6 py-3 rounded-full transition-colors border border-white/20">
                <Phone size={18} /> 076 200 36 12
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1B2B5B] mb-12 max-w-3xl" style={{ fontFamily: "Outfit, sans-serif" }}>{tt.whyUs}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="bg-white rounded-2xl p-6 border border-slate-200/60 hover:border-[#00C853]/40 hover:shadow-md transition-all">
                  <div className="w-11 h-11 rounded-xl bg-[#00C853]/10 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#00C853]" />
                  </div>
                  <h3 className="font-semibold text-[#1B2B5B] mb-2">{it.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{it.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1B2B5B] mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>{tt.neigh}</h2>
          <p className="text-slate-600 mb-10">AyaPay couvre {data.name} et ses environs.</p>
          <div className="flex flex-wrap gap-2.5 mb-16">
            {data.neighborhoods.map((n) => (
              <span key={n} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-100 text-slate-700 text-sm border border-slate-200">
                <MapPin size={14} className="text-[#00C853]" /> {n}
              </span>
            ))}
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-[#1B2B5B] mb-10" style={{ fontFamily: "Outfit, sans-serif" }}>{tt.sectors}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.sectors.map((s) => (
              <div key={s} className="flex items-center gap-2 p-4 rounded-xl bg-slate-50 border border-slate-200/60">
                <CheckCircle2 size={18} className="text-[#00C853] shrink-0" />
                <span className="text-sm font-medium text-slate-700">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 lg:py-24 bg-[#1B2B5B] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <blockquote className="text-2xl lg:text-3xl font-medium leading-snug mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            « {data.testimonial.text} »
          </blockquote>
          <p className="text-slate-300 text-sm">— {data.testimonial.name}, <span className="text-[#00C853]">{data.testimonial.business}</span></p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-[#0F1B3F] to-[#1B2B5B] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            Commerçant à {data.name} ? Équipez-vous gratuitement.
          </h2>
          <p className="text-slate-300 mb-8 max-w-2xl mx-auto">Échange gratuit avec un expert AyaPay. Devis personnalisé sous 2h.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#00C853] hover:bg-[#00B248] text-white font-semibold px-7 py-3.5 rounded-full transition-colors">
              {tt.contact} <ArrowRight size={18} />
            </Link>
            <a href="mailto:contact@ayapay.ch" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white font-semibold px-7 py-3.5 rounded-full transition-colors border border-white/20">
              <Mail size={18} /> contact@ayapay.ch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
